import { FormEvent, StrictMode, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { api } from './lib/api'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { CustomersPage } from './pages/CustomersPage'
import { LicensesPage } from './pages/LicensesPage'
import { IssueLicensePage } from './pages/IssueLicensePage'
import { ModulesPage } from './pages/ModulesPage'
import { UsersPage } from './pages/UsersPage'
import { InstallationsPage } from './pages/InstallationsPage'
import { ApplicationsPage } from './pages/ApplicationsPage'
import { ApprovalsPage } from './pages/ApprovalsPage'
import { IssuancePage } from './pages/IssuancePage'
import { ActivationCenterPage } from './pages/ActivationCenterPage'
import { ProtocolTrustPage } from './pages/ProtocolTrustPage'
import { CommercialAuthorizationsPage } from './pages/CommercialAuthorizationsPage'
import { OperationsCenterPage } from './pages/OperationsCenterPage'
import { CustomerPortalPage } from './pages/CustomerPortalPage'
import { CustomerPortalLoginPage } from './pages/CustomerPortalLoginPage'
import { LicenseCasePage } from './pages/LicenseCasePage'
import { CustomerCasePage } from './pages/CustomerCasePage'
import { EmptyState } from './components/EmptyState'
import { FeishuWorkbenchPage } from './pages/FeishuWorkbenchPage'
import { FeishuTaskDetailPage } from './pages/FeishuTaskDetailPage'
import { PersonalTasksPage } from './pages/PersonalTasksPage'
import { DeliveryWorkbenchPage } from './pages/DeliveryWorkbenchPage'
import { can } from './lib/permissions'
import { FeishuOperationPasswordSetup } from './components/FeishuOperationPasswordSetup'
import type { LicensePermission, Session } from './types'
import './styles.css'

function Protected() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [checking, setChecking] = useState(true)
  const [session, setSession] = useState<Session>({ authenticated: false })
  const [taskNotice, setTaskNotice] = useState<{ tone: 'pending' | 'success' | 'error', message: string }>()
  const handledTaskAction = useRef('')
  useEffect(() => {
    api.session().then(setSession).catch(() => setSession({ authenticated: false })).finally(() => setChecking(false))
  }, [])
  const taskId = searchParams.get('taskId')
  const taskAction = searchParams.get('taskAction')
  useEffect(() => {
    if (session.authenticated && taskId) void api.markOutboxEventRead(taskId).catch(() => undefined)
  }, [session.authenticated, taskId])
  useEffect(() => {
    if (!session.authenticated || !taskId || !['claim', 'acknowledge'].includes(taskAction || '')) return
    const actionKey = `${taskId}:${taskAction}`
    if (handledTaskAction.current === actionKey) return
    handledTaskAction.current = actionKey
    setTaskNotice({ tone: 'pending', message: taskAction === 'claim' ? '正在确认任务认领...' : '正在确认任务闭环...' })
    const action = taskAction === 'claim' ? api.claimOutboxEvent(taskId) : api.acknowledgeOutboxEvent(taskId)
    void action.then(() => setTaskNotice({
      tone: 'success',
      message: taskAction === 'claim' ? '任务已由你认领，后续办理结果会自动回写。' : '任务已确认完成并闭环。',
    })).catch(value => setTaskNotice({
      tone: 'error', message: value instanceof Error ? value.message : '任务操作失败，请进入责任队列检查。',
    })).finally(() => {
      const next = new URLSearchParams(searchParams)
      next.delete('taskAction')
      setSearchParams(next, { replace: true })
    })
  }, [searchParams, session.authenticated, setSearchParams, taskAction, taskId])
  if (checking) return null
  if (!session.authenticated) {
    const returnTo = `${location.pathname}${location.search}`
    return location.pathname.startsWith('/tasks')
      ? <Navigate to={`/feishu?returnTo=${encodeURIComponent(returnTo)}`} replace />
      : <Navigate to="/login" replace />
  }
  if (session.passwordChangeRequired && session.authenticationMethod === 'FEISHU') {
    return <FeishuOperationPasswordSetup session={session} returnTo={`${location.pathname}${location.search}`} />
  }
  if (session.passwordChangeRequired) return <InitialPasswordChange username={session.username || ''} />
  return <><AppShell />{taskNotice && <div className={`task-action-notice task-action-notice--${taskNotice.tone}`} role="status"><span>{taskNotice.message}</span><button type="button" onClick={() => setTaskNotice(undefined)} aria-label="关闭任务提示">×</button></div>}</>
}

function PermissionRoute({ permission }: { permission: LicensePermission }) {
  const [checking, setChecking] = useState(true)
  const [session, setSession] = useState<Session>({ authenticated: false })

  useEffect(() => {
    api.session().then(setSession).catch(() => setSession({ authenticated: false }))
      .finally(() => setChecking(false))
  }, [])

  if (checking) return <EmptyState title="正在确认页面权限" detail="" />
  if (!can(session, permission)) {
    return <EmptyState title="当前账号无此页面访问权限" detail="请联系用户管理员分配相应业务能力。" />
  }
  return <Outlet />
}

function AppRoutes() {
  const navigate = useNavigate()
  const [, refresh] = useState(0)
  useEffect(() => {
    const expired = () => { refresh(v => v + 1); navigate('/login') }
    const portalExpired = () => { refresh(v => v + 1); navigate('/portal/login', { replace: true }) }
    window.addEventListener('auth:expired', expired)
    window.addEventListener('portal:auth-expired', portalExpired)
    return () => { window.removeEventListener('auth:expired', expired); window.removeEventListener('portal:auth-expired', portalExpired) }
  }, [navigate])
  useEffect(() => { void api.csrf().catch(() => undefined) }, [])
  return <Routes>
    <Route path="/feishu" element={<FeishuWorkbenchPage />} />
    <Route path="/feishu/tasks/:taskId" element={<FeishuTaskDetailPage />} />
    <Route path="/portal/login" element={<CustomerPortalLoginPage />} />
    <Route path="/portal" element={<CustomerPortalPage />} />
    <Route path="/login" element={<LoginPage />} /><Route element={<Protected />}>
    <Route element={<PermissionRoute permission="DASHBOARD_VIEW" />}><Route path="/" element={<DashboardPage />} /></Route>
    <Route path="/tasks" element={<PersonalTasksPage />} />
    <Route element={<PermissionRoute permission="LICENSE_VIEW" />}><Route path="/deliveries" element={<DeliveryWorkbenchPage />} /></Route>
    <Route path="/tasks/:taskId" element={<FeishuTaskDetailPage platform />} />
    <Route element={<PermissionRoute permission="CUSTOMER_VIEW" />}><Route path="/customers" element={<CustomersPage />} /></Route>
    <Route element={<PermissionRoute permission="CUSTOMER_VIEW" />}><Route path="/customer-cases/:customerId" element={<CustomerCasePage />} /></Route>
    <Route element={<PermissionRoute permission="COMMERCIAL_AUTHORIZATION_VIEW" />}><Route path="/commercial-authorizations" element={<CommercialAuthorizationsPage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_VIEW" />}><Route path="/applications" element={<ApplicationsPage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_VIEW" />}><Route path="/license-cases/:licenseId" element={<LicenseCasePage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_REQUEST" />}><Route path="/applications/new" element={<IssueLicensePage />} /><Route path="/licenses/new" element={<IssueLicensePage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_APPROVE" />}><Route path="/approvals" element={<ApprovalsPage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_ISSUE" />}><Route path="/issuance" element={<IssuancePage />} /></Route>
    <Route element={<PermissionRoute permission="ACTIVATION_VIEW" />}><Route path="/activations" element={<ActivationCenterPage />} /></Route>
    <Route element={<PermissionRoute permission="PROTOCOL_VIEW" />}><Route path="/protocol" element={<ProtocolTrustPage />} /></Route>
    <Route element={<PermissionRoute permission="CUSTOMER_PORTAL_MANAGE" />}><Route path="/operations" element={<OperationsCenterPage />} /></Route>
    <Route element={<PermissionRoute permission="LICENSE_VIEW" />}><Route path="/licenses" element={<LicensesPage />} /></Route>
    <Route element={<PermissionRoute permission="INSTALLATION_VIEW" />}><Route path="/installations" element={<InstallationsPage />} /></Route>
    <Route element={<PermissionRoute permission="PRODUCT_CATALOG_VIEW" />}><Route path="/modules" element={<ModulesPage />} /></Route>
    <Route element={<PermissionRoute permission="USER_MANAGE" />}><Route path="/users" element={<UsersPage />} /></Route>
  </Route><Route path="*" element={<Navigate to="/" replace />} /></Routes>
}

function InitialPasswordChange({ username }: { username: string }) {
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (newPassword !== confirmPassword) { setError('两次输入的新密码不一致'); return }
    setSaving(true); setError('')
    try {
      await api.changeOwnPassword(currentPassword, newPassword)
      await api.logout().catch(() => undefined)
      navigate('/login', { replace: true })
    } catch (value) {
      setError(value instanceof Error ? value.message : '密码修改失败')
    } finally { setSaving(false) }
  }

  return <main className="login-page"><section className="login-panel" aria-labelledby="initial-password-title"><div className="login-brand"><b>License Center</b><span>SECURITY ONBOARDING</span></div><div className="login-intro"><h1 id="initial-password-title">请先设置新密码</h1><p>{username ? `账号 ${username} 使用的是初始或已重置密码。` : '当前账号使用的是初始或已重置密码。'} 为保护岗位权限，修改完成后请使用新密码重新登录。</p></div><form onSubmit={submit} className="login-form"><label>当前临时密码<input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} autoFocus autoComplete="current-password" required /></label><label>新密码<input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" minLength={8} required /></label><label>确认新密码<input type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" minLength={8} required /></label>{error && <div className="form-error">{error}</div>}<button className="button button--primary" disabled={saving}>{saving ? '正在更新...' : '设置新密码并重新登录'}</button></form></section></main>
}

createRoot(document.getElementById('root')!).render(<StrictMode><BrowserRouter><AppRoutes /></BrowserRouter></StrictMode>)
